/**
 * Admin Events page (Slice 2 CP8 Phase 2).
 *
 * Replaces the CP3 read-only view with a create + edit surface sourced
 * from `/v2/staging/recap-events/admin/list.json`. The admin shape is
 * flat — parent_event_id is a column rather than a nested `subentries`
 * array — because editors need to see every row (incl. unreviewed /
 * hidden / flagged) without the public filter.
 *
 * Scope is tightly bounded per the CP8 plan §IN/OUT:
 *   • create + edit only (no delete, no review_state flip)
 *   • no approval controls; no published_at / promoted_to_challenge_id
 *   • no source/evidence UI; no media flow
 *   • scope + arc_id are set at create time only and never editable
 *   • parent/subentry: parent_event_id whitelisted on edit, but only
 *     rows that share the child's arc_id are offered as options
 *
 * Idempotency: one `crypto.randomUUID()` per form open, reused across
 * any retries. A retry after a network blip replays on the Worker's
 * `mutation_events.idempotency_key` UNIQUE → the prior receipt is
 * returned without writing a second audit row.
 *
 * Delta-only PATCH: the edit form builds a body containing only the
 * fields actually changed vs the row snapshot the editor loaded. This
 * keeps the Worker's whitelist-strict parser from seeing fields the
 * editor didn't touch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import type {
  ArcAdminListArtifact,
  ArcAdminListItem,
  EntitiesAdminListItem,
  RecapEventCreateRequest,
  RecapEventDeleteReceipt,
  RecapEventEditRequest,
  RecapEventEntityAdminItem,
  RecapEventEntityValidationError,
  RecapEventForAdmin,
  RecapEventReviewStateValidationError,
  RecapEventTypeAdminItem,
  RecapEventValidationError,
  RecapEventsForAdminListArtifact,
  RecapReviewState,
} from '@fishstank/contracts'
import { RECAP_EVENT_ENTITY_ROLE_MAX_LENGTH } from '@fishstank/contracts'
import {
  deleteRecapEvent,
  deleteRecapEventEntity,
  fetchArcsForAdmin,
  fetchEntitiesForAdmin,
  fetchRecapEventsAdminList,
  type FetchResult,
  type MutationFetchResult,
  type RecapEventDeleteFetchResult,
  patchRecapEvent,
  patchRecapEventReviewState,
  postRecapEvent,
  postRecapEventEntity,
} from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

type RecapEventMutationReceipt = {
  body: {
    row: RecapEventForAdmin
    prior_row: RecapEventForAdmin | null
    mutation_event_id: string
    audit_mirror_deferred?: boolean
  }
}

// crypto.randomUUID is widely available in modern browsers + jsdom;
// for the test environment we wrap it so a shim could replace it.
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

// ────────────────────────────────────────────────────────────────────────────
// Shared utilities
// ────────────────────────────────────────────────────────────────────────────

function validationMessage(err: RecapEventValidationError): string {
  switch (err.code) {
    case 'title_required':
      return 'Title is required.'
    case 'title_too_long':
      return `Title is too long (max ${err.max} characters).`
    case 'body_md_too_long':
      return `Body is too long (max ${err.max} characters).`
    case 'invalid_enum':
      return `Invalid value for "${err.field}" (allowed: ${err.allowed.join(', ')}).`
    case 'invalid_uuid':
      return `Internal error: ${err.field} must be a UUID. Refresh and try again.`
    case 'invalid_iso':
      return `"${err.field}" must be an ISO-8601 date (e.g. 2026-01-02T18:00:00Z).`
    case 'invalid_integer':
      return `"${err.field}" must be an integer.`
    case 'invalid_parent':
      return 'Parent event id is not a valid numeric id.'
    case 'scope_arc_mismatch':
      return 'Arc-scoped events require an arc_id; global events must not have one.'
    case 'subentry_depth_exceeded':
      return 'Recap events only support a single layer of subentries.'
    case 'subentry_arc_mismatch':
      return 'Subentry arc must match its parent arc.'
    case 'unknown_event_type':
      return 'Selected event type is not known.'
    case 'unknown_parent':
      return 'Selected parent event is not known.'
    case 'no_editable_fields':
      return 'Nothing changed. Modify at least one field before submitting.'
    case 'not_found':
      return 'Event not found.'
    default:
      return 'Validation failed.'
  }
}

function fieldKey(err: RecapEventValidationError): string | null {
  if ('field' in err) return err.field
  return null
}

function deriveArcOptions(items: readonly RecapEventForAdmin[]): Array<{
  arc_id: string
  slug: string
  title: string
}> {
  const seen = new Map<string, { arc_id: string; slug: string; title: string }>()
  for (const r of items) {
    if (r.arc_id && r.arc && !seen.has(r.arc_id)) {
      seen.set(r.arc_id, { arc_id: r.arc_id, slug: r.arc.slug, title: r.arc.title })
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * Fallback event-type options when the artifact body doesn't carry
 * `event_types` (older worker, or if the field is missing for any
 * reason). Scrapes the unique types already used by visible rows.
 * The post-this-slice worker always emits `event_types` so this is
 * defensive only.
 */
function deriveEventTypeOptionsFromItems(
  items: readonly RecapEventForAdmin[],
): RecapEventTypeAdminItem[] {
  const seen = new Map<string, RecapEventTypeAdminItem>()
  for (const r of items) {
    if (r.event_type_id && r.event_type && !seen.has(r.event_type_id)) {
      seen.set(r.event_type_id, {
        id: r.event_type_id,
        slug: r.event_type.slug,
        label: r.event_type.label,
      })
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
}

function deriveParentOptions(
  items: readonly RecapEventForAdmin[],
  arcId: string | null,
  excludeId: string | null,
): readonly RecapEventForAdmin[] {
  return items.filter(
    (r) =>
      r.parent_event_id === null &&
      r.id !== excludeId &&
      r.arc_id === arcId &&
      arcId !== null,
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Chronology helpers (admin Events usability slice)
//
// `dayOfSeason` returns "Day N" relative to the arc's start_date, using
// UTC-date math so timezone wobble doesn't shift events across day
// boundaries. Returns null when either side of the subtraction is
// missing — global events without a happened_at, arcs with no
// start_date, etc.
//
// `formatHappenedAt` produces a deterministic, locale-free string for
// the "When" column. Format: "Mar 15, 2026 · 14:30 UTC". Cheap,
// SSR-safe, no Intl involvement.
// ────────────────────────────────────────────────────────────────────────────

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export function dayOfSeason(
  arcStartDate: string | null,
  happenedAt: string | null,
): number | null {
  if (!arcStartDate || !happenedAt) return null
  const start = Date.parse(arcStartDate)
  const event = Date.parse(happenedAt)
  if (Number.isNaN(start) || Number.isNaN(event)) return null
  const eventDay = new Date(event)
  eventDay.setUTCHours(0, 0, 0, 0)
  const startDay = new Date(start)
  startDay.setUTCHours(0, 0, 0, 0)
  const diffMs = eventDay.getTime() - startDay.getTime()
  if (diffMs < 0) return null
  return Math.floor(diffMs / 86400000) + 1
}

export function formatHappenedAt(iso: string | null): string | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  const d = new Date(t)
  const month = MONTH_ABBR[d.getUTCMonth()]
  const day = d.getUTCDate()
  const year = d.getUTCFullYear()
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${month} ${day}, ${year} · ${hh}:${mm} UTC`
}

/**
 * Date-only formatter for the day-group separator. Returns
 * "Mar 15, 2026" — drops the time component since the separator
 * spans every event that landed on that UTC date.
 */
export function formatDateOnly(iso: string | null): string | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  const d = new Date(t)
  const month = MONTH_ABBR[d.getUTCMonth()]
  const day = d.getUTCDate()
  const year = d.getUTCFullYear()
  return `${month} ${day}, ${year}`
}

// ────────────────────────────────────────────────────────────────────────────
// datetime-local <-> ISO conversion
//
// The admin form uses `<input type="datetime-local">` so the operator
// gets a real picker. The browser's input value format is
// `YYYY-MM-DDTHH:MM` (no timezone, no seconds). We treat that string
// as UTC end-to-end:
//
//   - Reading: take the row's ISO `happened_at`, format the UTC
//     components as `YYYY-MM-DDTHH:MM`. The input shows that
//     verbatim; what the operator sees is the UTC time, no local
//     offset applied.
//   - Writing: take the input value and append `:00Z` to get back
//     to a sortable, server-acceptable ISO. Round-trip is lossless
//     at minute precision.
//
// This matches the previous free-text behavior (operators typed UTC
// ISO directly) while removing the manual-typing footgun.
// ────────────────────────────────────────────────────────────────────────────

export function isoToInputValue(iso: string | null): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const min = String(d.getUTCMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

export function inputValueToIso(value: string): string | null {
  const v = value.trim()
  if (v.length === 0) return null
  // The browser produces `YYYY-MM-DDTHH:MM`. Append `:00Z` to make
  // it a valid ISO-8601 UTC timestamp at second precision.
  return `${v}:00Z`
}

/**
 * UTC-date key (YYYY-MM-DD) for grouping. Returns null when the input
 * isn't a parseable timestamp. Used to detect day-group transitions
 * between adjacent rows in the admin events table.
 */
export function utcDateKey(iso: string | null): string | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  const d = new Date(t)
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}-${m}-${day}`
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// URL-state hook — reads ?page, ?arc_slug, ?jump_to_day from the
// browser address bar and exposes setters that rewrite the URL via
// `history.pushState` (so the page back-button works the way operators
// expect: each filter or page step is a history entry).
//
// We deliberately drive the query from URL state, not local React state,
// so reloads + back-navigation preserve the operator's place in long
// arcs. `jump_to_day` is one-shot — once the loader resolves it to a
// concrete page, the page rewrites the URL to `?page=<resolved>` and
// clears `jump_to_day` so the URL stays stable.
// ────────────────────────────────────────────────────────────────────────────

interface EventsUrlState {
  page: number
  arcSlug: string | null
  jumpToDay: number | null
}

function readEventsUrlState(): EventsUrlState {
  if (typeof window === 'undefined') {
    return { page: 1, arcSlug: null, jumpToDay: null }
  }
  const sp = new URL(window.location.href).searchParams
  const rawPage = sp.get('page')
  const page = rawPage ? Math.max(1, Number.parseInt(rawPage, 10) || 1) : 1
  const arcSlug = sp.get('arc_slug')?.trim() || null
  const rawJump = sp.get('jump_to_day')
  const jumpToDay = rawJump
    ? Math.max(1, Number.parseInt(rawJump, 10) || 0) || null
    : null
  return { page, arcSlug, jumpToDay }
}

function writeEventsUrlState(patch: Partial<EventsUrlState>): EventsUrlState {
  const cur = readEventsUrlState()
  const next = { ...cur, ...patch }
  if (typeof window === 'undefined') return next
  const u = new URL(window.location.href)
  if (next.page > 1) u.searchParams.set('page', String(next.page))
  else u.searchParams.delete('page')
  if (next.arcSlug) u.searchParams.set('arc_slug', next.arcSlug)
  else u.searchParams.delete('arc_slug')
  if (next.jumpToDay) u.searchParams.set('jump_to_day', String(next.jumpToDay))
  else u.searchParams.delete('jump_to_day')
  window.history.pushState({}, '', u.toString())
  return next
}

export function EventsPage() {
  const qc = useQueryClient()
  const [urlState, setUrlState] = useState<EventsUrlState>(() =>
    readEventsUrlState(),
  )

  // Browser back/forward — re-read URL so the table refetches.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onPop = () => setUrlState(readEventsUrlState())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const query = useQuery({
    queryKey: [
      'recap-events',
      'admin',
      'list',
      {
        page: urlState.page,
        arcSlug: urlState.arcSlug,
        jumpToDay: urlState.jumpToDay,
      },
    ],
    queryFn: () =>
      fetchRecapEventsAdminList({
        page: urlState.page,
        arcSlug: urlState.arcSlug,
        jumpToDay: urlState.jumpToDay,
      }),
  })

  const result = query.data
  const items: readonly RecapEventForAdmin[] = useMemo(() => {
    if (!result) return []
    if (result.kind === 'ok' || result.kind === 'empty') {
      return result.artifact.body.items
    }
    return []
  }, [result])
  const pagination = useMemo(() => {
    if (!result || !(result.kind === 'ok' || result.kind === 'empty')) {
      return null
    }
    return result.artifact.body.pagination
  }, [result])

  // After a jump_to_day request resolves, rewrite URL to the resolved
  // page and clear jump_to_day so reload doesn't re-resolve.
  useEffect(() => {
    if (urlState.jumpToDay && pagination && pagination.page) {
      const next = writeEventsUrlState({
        page: pagination.page,
        jumpToDay: null,
      })
      setUrlState(next)
    }
  }, [pagination, urlState.jumpToDay])

  // Full arcs list for the filter dropdown + CreateEventForm. Cached
  // separately so the filter UI doesn't churn when paging.
  const arcsListQuery = useQuery<FetchResult<ArcAdminListArtifact>>({
    queryKey: ['arcs', 'admin', 'list'],
    queryFn: () => fetchArcsForAdmin(),
    staleTime: 60_000,
  })
  const allArcs: readonly ArcAdminListItem[] = useMemo(() => {
    const r = arcsListQuery.data
    if (r && r.kind === 'ok') return r.artifact.body.arcs
    return []
  }, [arcsListQuery.data])
  const arcOptions = useMemo(() => {
    // CreateEventForm prop shape: { arc_id, slug, title }. Sourced from
    // the closed list now (not just the events page) so create still
    // sees every arc when the operator paginates.
    if (allArcs.length > 0) {
      return allArcs
        .map((a) => ({ arc_id: a.id, slug: a.slug, title: a.title }))
        .sort((a, b) => a.title.localeCompare(b.title))
    }
    return deriveArcOptions(items)
  }, [allArcs, items])
  const selectedArc = useMemo(
    () => allArcs.find((a) => a.slug === urlState.arcSlug) ?? null,
    [allArcs, urlState.arcSlug],
  )
  // Prefer the closed list the loader emits in `body.event_types` —
  // every type the DB knows about, not just the ones already used by
  // the current page. Fall back to scraping items when the field is
  // missing (older worker / defensive guard).
  const eventTypeOptions: RecapEventTypeAdminItem[] = useMemo(() => {
    if (
      result &&
      (result.kind === 'ok' || result.kind === 'empty') &&
      Array.isArray(result.artifact.body.event_types)
    ) {
      return result.artifact.body.event_types
    }
    return deriveEventTypeOptionsFromItems(items)
  }, [result, items])

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['recap-events', 'admin', 'list'] })

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">Events</h1>
          <p className="mt-1 text-sm text-slate-400">
            Admin view. Every <code className="text-slate-300">review_state</code>{' '}
            surfaces here. Create + edit only — approval controls land in a
            later slice.
          </p>
        </div>
        {!createOpen && (
          <button
            type="button"
            data-testid="events-create-open"
            onClick={() => setCreateOpen(true)}
            className="rounded-md bg-fishstank-accent px-3 py-1.5 text-sm font-semibold text-fishstank-ink hover:brightness-110"
          >
            + Create event
          </button>
        )}
      </header>

      {createOpen && (
        <CreateEventForm
          arcOptions={arcOptions}
          eventTypeOptions={eventTypeOptions}
          items={items}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            invalidate()
            setCreateOpen(false)
          }}
        />
      )}

      <EventsListControls
        urlState={urlState}
        allArcs={allArcs}
        selectedArc={selectedArc}
        pagination={pagination}
        onChange={(patch) => setUrlState(writeEventsUrlState(patch))}
      />

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="events"
        labels={{
          loading: 'Loading events…',
          errorTitle: "Couldn't load events.",
          // `empty` intentionally omitted — the `kind:'empty'` case
          // falls through to `children(artifact)` so the inner
          // AdminEventsTable renders the CTA-bearing empty state
          // ("No events yet. Use 'Create event' above to add the
          // first one.") at its own layer. See
          // components/FetchedArtifact.tsx module comment.
        }}
      >
        {() => (
          <AdminEventsTable
            items={items}
            eventTypeOptions={eventTypeOptions}
            editingId={editingId}
            onStartEdit={setEditingId}
            onStopEdit={() => setEditingId(null)}
            onSaved={() => {
              invalidate()
              setEditingId(null)
            }}
          />
        )}
      </FetchedArtifact>

      <EventsListPager
        urlState={urlState}
        pagination={pagination}
        onChange={(patch) => setUrlState(writeEventsUrlState(patch))}
      />
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EventsListControls — arc filter + jump-to-day input above the table.
//
// Arc filter narrows the loader's WHERE clause. Day-jump is only
// meaningful inside an arc (day numbers are arc-relative), so the
// input is disabled until an arc is selected. Submission triggers a
// fetch with `jump_to_day=N`; the loader resolves it server-side and
// the page rewrites URL state to `?page=<resolved>` so reload + back
// nav land cleanly.
// ────────────────────────────────────────────────────────────────────────────

function EventsListControls({
  urlState,
  allArcs,
  selectedArc,
  pagination,
  onChange,
}: {
  urlState: EventsUrlState
  allArcs: readonly ArcAdminListItem[]
  selectedArc: ArcAdminListItem | null
  pagination: {
    page: number
    page_size: number
    total_returned: number
    has_next: boolean
    truncated: boolean
  } | null
  onChange: (patch: Partial<EventsUrlState>) => void
}) {
  const [dayInput, setDayInput] = useState<string>('')
  // Whether the day-jump submit is enabled. Requires an arc and a
  // positive integer that's also within the arc's known span (when
  // we know it).
  const dayCount = useMemo(() => {
    if (!selectedArc?.start_date || !selectedArc.end_date) return null
    const startMs = Date.parse(`${selectedArc.start_date}T00:00:00Z`)
    const endMs = Date.parse(`${selectedArc.end_date}T00:00:00Z`)
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null
    return Math.max(1, Math.floor((endMs - startMs) / 86400000) + 1)
  }, [selectedArc])

  function submitJump(e: React.FormEvent) {
    e.preventDefault()
    const parsed = Number.parseInt(dayInput.trim(), 10)
    if (!Number.isFinite(parsed) || parsed < 1) return
    if (!urlState.arcSlug) return
    onChange({ jumpToDay: parsed })
    setDayInput('')
  }

  return (
    <section
      data-testid="events-list-controls"
      className="mb-3 flex flex-col gap-2 rounded-md border border-slate-800 bg-fishstank-surface p-3 sm:flex-row sm:items-center sm:gap-4"
    >
      <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">
        Arc
        <select
          data-testid="events-arc-filter"
          value={urlState.arcSlug ?? ''}
          onChange={(e) =>
            onChange({
              arcSlug: e.target.value || null,
              // Reset to page 1 on arc change.
              page: 1,
              jumpToDay: null,
            })
          }
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-xs normal-case tracking-normal text-slate-100"
        >
          <option value="">All arcs</option>
          {allArcs.map((a) => (
            <option key={a.id} value={a.slug}>
              {a.title} ({a.slug})
            </option>
          ))}
        </select>
      </label>

      <form
        onSubmit={submitJump}
        data-testid="events-jump-to-day-form"
        className="flex items-center gap-2"
      >
        <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">
          Jump to day
          <input
            type="number"
            data-testid="events-jump-to-day-input"
            min={1}
            max={dayCount ?? undefined}
            value={dayInput}
            onChange={(e) => setDayInput(e.target.value)}
            disabled={!urlState.arcSlug}
            placeholder={
              urlState.arcSlug
                ? dayCount
                  ? `1–${dayCount}`
                  : 'day'
                : 'select arc first'
            }
            className="w-28 rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-xs normal-case tracking-normal text-slate-100 disabled:opacity-40"
          />
        </label>
        <button
          type="submit"
          data-testid="events-jump-to-day-submit"
          disabled={!urlState.arcSlug || dayInput.trim() === ''}
          className="rounded-sm border border-slate-700 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-slate-200 hover:bg-slate-800 disabled:opacity-40"
        >
          Go
        </button>
      </form>

      {pagination && (
        <span
          data-testid="events-result-count"
          className="ml-auto text-[10px] font-mono uppercase tracking-wider text-slate-500"
        >
          Page {pagination.page} · {pagination.total_returned} on this page
          {pagination.truncated && (
            <span
              className="ml-2 text-amber-400"
              title="The loader's hard cap was hit; some events may not be reachable via paging."
            >
              · truncated
            </span>
          )}
        </span>
      )}
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EventsListPager — Prev / Next / page indicator below the table.
//
// Renders nothing when there's only one page of results (no
// `has_next` AND we're already on page 1). Disables Prev on page 1
// and Next when `has_next === false`. The page indicator doubles as
// a quick numeric input — operators familiar with the arc can type
// a page number directly.
// ────────────────────────────────────────────────────────────────────────────

function EventsListPager({
  urlState,
  pagination,
  onChange,
}: {
  urlState: EventsUrlState
  pagination: {
    page: number
    page_size: number
    total_returned: number
    has_next: boolean
    truncated: boolean
  } | null
  onChange: (patch: Partial<EventsUrlState>) => void
}) {
  const [pageInput, setPageInput] = useState<string>('')
  if (!pagination) return null
  const currentPage = pagination.page
  const onlyOnePage = currentPage === 1 && !pagination.has_next
  if (onlyOnePage) return null

  function go(pageNum: number) {
    const next = Math.max(1, pageNum)
    onChange({ page: next, jumpToDay: null })
  }

  function submitPageInput(e: React.FormEvent) {
    e.preventDefault()
    const parsed = Number.parseInt(pageInput.trim(), 10)
    if (!Number.isFinite(parsed) || parsed < 1) return
    go(parsed)
    setPageInput('')
  }

  return (
    <nav
      data-testid="events-pager"
      data-page={currentPage}
      className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-300"
    >
      <button
        type="button"
        data-testid="events-pager-first"
        onClick={() => go(1)}
        disabled={currentPage === 1}
        className="rounded-sm border border-slate-700 px-2 py-1 hover:bg-slate-800 disabled:opacity-40"
      >
        ‹‹ First
      </button>
      <button
        type="button"
        data-testid="events-pager-prev"
        onClick={() => go(currentPage - 1)}
        disabled={currentPage === 1}
        className="rounded-sm border border-slate-700 px-2 py-1 hover:bg-slate-800 disabled:opacity-40"
      >
        ‹ Prev
      </button>
      <span
        data-testid="events-pager-current"
        className="font-mono text-slate-200"
      >
        Page {currentPage}
      </span>
      <button
        type="button"
        data-testid="events-pager-next"
        onClick={() => go(currentPage + 1)}
        disabled={!pagination.has_next}
        className="rounded-sm border border-slate-700 px-2 py-1 hover:bg-slate-800 disabled:opacity-40"
      >
        Next ›
      </button>

      <form
        onSubmit={submitPageInput}
        data-testid="events-pager-jump-form"
        className="ml-2 flex items-center gap-1"
      >
        <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
          Go to page
        </label>
        <input
          type="number"
          min={1}
          data-testid="events-pager-jump-input"
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          placeholder={String(currentPage)}
          className="w-16 rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-xs text-slate-100"
        />
      </form>

      {urlState.arcSlug === null && (
        <span className="ml-3 text-[10px] uppercase tracking-wider text-slate-600">
          Tip: select an arc above to enable Jump to day.
        </span>
      )}
    </nav>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────

function AdminEventsTable({
  items,
  eventTypeOptions,
  editingId,
  onStartEdit,
  onStopEdit,
  onSaved,
}: {
  items: readonly RecapEventForAdmin[]
  eventTypeOptions: readonly RecapEventTypeAdminItem[]
  editingId: string | null
  onStartEdit: (id: string) => void
  onStopEdit: () => void
  onSaved: () => void
}) {
  if (items.length === 0) {
    return (
      <div
        data-testid="events-empty"
        className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
      >
        No events yet. Use "Create event" above to add the first one.
      </div>
    )
  }
  // Pre-walk the items so each row knows whether it leads a new day
  // group (its UTC-date key differs from the previous row's). The list
  // is already happened_at DESC NULLS LAST, id DESC server-side, so
  // separators chunk events into "today / yesterday / earlier" by date.
  // Rows where happened_at is null share the same null bucket and emit
  // a single "Undated" separator at first encounter.
  const dayLeaders: boolean[] = items.map((row, idx) => {
    if (idx === 0) return true
    return utcDateKey(row.happened_at) !== utcDateKey(items[idx - 1]!.happened_at)
  })

  // Total column count for the day-separator <tr colspan>. Keep in
  // sync with the <thead> below: Day, When, Title, Type, Arc/Scope,
  // State, (action) = 7.
  const COLSPAN = 7

  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="events-table"
        data-event-count={items.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-20">Day</th>
            <th className="px-3 py-2 text-left font-medium w-44">When</th>
            <th className="px-3 py-2 text-left font-medium">Title</th>
            <th className="px-3 py-2 text-left font-medium w-32">Type</th>
            <th className="px-3 py-2 text-left font-medium w-32">Arc</th>
            <th className="px-3 py-2 text-left font-medium w-32">State</th>
            <th className="px-3 py-2 text-left font-medium w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {items.map((row, idx) => {
            const isDayLeader = dayLeaders[idx]
            const day = dayOfSeason(row.arc_start_date, row.happened_at)
            const dateKey = utcDateKey(row.happened_at)
            const fragments: React.ReactNode[] = []

            // Day-group separator row. Renders before the first row of
            // each new UTC-day bucket. Shows "Day N · Mar 15, 2026" when
            // arc start_date is known; falls back to just the date or
            // "Undated" when not.
            if (isDayLeader) {
              const dayLabel =
                day !== null
                  ? `Day ${day}`
                  : dateKey === null
                    ? 'Undated'
                    : null
              const dateLabel =
                row.happened_at !== null
                  ? formatDateOnly(row.happened_at)
                  : null
              fragments.push(
                <tr
                  key={`sep-${idx}`}
                  data-testid="events-day-separator"
                  data-day={day ?? ''}
                  data-date-key={dateKey ?? ''}
                  className="bg-slate-950/60"
                >
                  <td
                    colSpan={COLSPAN}
                    className="px-3 py-1.5 text-xs"
                  >
                    <div className="flex items-baseline gap-3">
                      {dayLabel && (
                        <span className="font-mono text-fishstank-accent">
                          {dayLabel}
                        </span>
                      )}
                      {dateLabel && (
                        <span className="text-slate-500">{dateLabel}</span>
                      )}
                      {dayLabel === null && dateLabel === null && (
                        <span className="text-slate-600">Undated</span>
                      )}
                    </div>
                  </td>
                </tr>,
              )
            }

            // Editing-row collapses the normal cells in favor of the
            // EditEventForm; it still gets the day separator above.
            if (editingId === row.id) {
              fragments.push(
                <tr
                  key={row.id}
                  data-testid="events-edit-row"
                  data-event-id={row.id}
                >
                  <td colSpan={COLSPAN} className="p-0">
                    <EditEventForm
                      row={row}
                      items={items}
                      eventTypeOptions={eventTypeOptions}
                      onCancel={onStopEdit}
                      onSaved={onSaved}
                    />
                  </td>
                </tr>,
              )
            } else {
              fragments.push(
                <AdminEventRow
                  key={row.id}
                  row={row}
                  day={day}
                  onStartEdit={() => onStartEdit(row.id)}
                  onReviewStateChanged={onSaved}
                  onDeleted={onSaved}
                />,
              )
            }

            return fragments
          })}
        </tbody>
      </table>
    </div>
  )
}

function AdminEventRow({
  row,
  day,
  onStartEdit,
  onReviewStateChanged,
  onDeleted,
}: {
  row: RecapEventForAdmin
  day: number | null
  onStartEdit: () => void
  onReviewStateChanged: () => void
  onDeleted: () => void
}) {
  const formattedWhen = formatHappenedAt(row.happened_at)
  return (
    <tr
      data-testid="events-row"
      data-event-id={row.id}
      data-scope={row.scope}
      data-review-state={row.review_state}
      data-day={day ?? ''}
      className="hover:bg-slate-900/40 align-top"
    >
      {/* Day badge — primary chronology cue. Falls back to "—" when
          the event has no resolvable day (no arc start_date or no
          happened_at). */}
      <td className="px-3 py-2">
        {day !== null ? (
          <span
            data-testid="events-day-badge"
            data-day-value={day}
            className="inline-flex items-center rounded border border-fishstank-accent/60 bg-fishstank-accent/10 px-2 py-0.5 font-mono text-xs text-fishstank-accent"
          >
            Day {day}
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>

      {/* When — formatted timestamp. Secondary chronology context. */}
      <td
        className="px-3 py-2 font-mono text-xs text-slate-400"
        data-testid="events-when"
        data-happened-at={row.happened_at ?? ''}
      >
        {formattedWhen ?? <span className="text-slate-600">—</span>}
      </td>

      {/* Title cell — the row's primary content. The bigint id moves
          here as a small dim suffix so it remains discoverable
          without taking column real estate. parent_event_id is also
          surfaced inline rather than as its own column. */}
      <td className="px-3 py-2 text-slate-100">
        <div className="flex flex-col gap-0.5">
          <span data-testid="events-title">{row.title}</span>
          <span className="font-mono text-[10px] text-slate-600">
            <span data-testid="events-id">#{row.id}</span>
            {row.parent_event_id !== null && (
              <span className="ml-2" data-testid="events-parent">
                ↳ child of #{row.parent_event_id}
              </span>
            )}
          </span>
        </div>
      </td>

      {/* Event type chip. Always renders a placeholder when null so
          the column doesn't visually collapse. */}
      <td className="px-3 py-2">
        {row.event_type ? (
          <span
            data-testid="events-type-chip"
            data-event-type-slug={row.event_type.slug}
            className="inline-flex items-center rounded-sm border border-slate-700 bg-slate-900 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-200"
          >
            {row.event_type.label}
          </span>
        ) : (
          <span className="text-slate-600 text-xs">—</span>
        )}
      </td>

      {/* Arc / scope. Combined into one column: arc slug primary,
          'global' badge replaces it for scope='global' rows. */}
      <td className="px-3 py-2">
        {row.scope === 'global' ? (
          <span
            data-testid="events-scope-global"
            className="rounded-sm bg-fishstank-accent/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fishstank-accent"
          >
            Global
          </span>
        ) : row.arc ? (
          <span
            className="inline-flex flex-col text-xs"
            data-testid="events-arc"
          >
            <span className="text-slate-200">{row.arc.title}</span>
            <span className="font-mono text-[10px] text-slate-500">
              {row.arc.slug}
            </span>
          </span>
        ) : (
          <span className="text-slate-600 text-xs">—</span>
        )}
      </td>

      <td className="px-3 py-2">
        <ReviewStateCell row={row} onChanged={onReviewStateChanged} />
      </td>

      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center gap-1.5">
          <button
            type="button"
            data-testid="events-edit-open"
            data-event-id={row.id}
            onClick={onStartEdit}
            className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
          >
            Edit
          </button>
          <EventDeleteControl row={row} onDeleted={onDeleted} />
        </span>
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Delete control (slice 3)
//
// Inline confirm pattern lifted from the arc-participation delete row
// in ArcEdit.tsx — single button click opens an inline confirm/cancel
// pair in the same cell, second click submits the DELETE. One
// crypto.randomUUID() per confirm-open so a double-click of the
// confirm button replays the same audit row instead of writing a
// second one. Children (subentries via parent_event_id, attachments,
// sources) cascade at the DB level — no client-side fan-out.
//
// On 404 (recap_event_not_found) we treat the row as already gone:
// trigger the parent refresh and clear local state. The DB is the
// authority either way.
// ────────────────────────────────────────────────────────────────────────────

function EventDeleteControl({
  row,
  onDeleted,
}: {
  row: RecapEventForAdmin
  onDeleted: () => void
}) {
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)
  // Pinned per confirm-open lifecycle — a fresh key when the operator
  // opens the confirm, reused if they double-click the Confirm button.
  const idempotencyKey = useMemo(
    () => globalThis.crypto.randomUUID(),
    // Rotate on each new confirm-open. We deliberately use confirmOpen
    // in the dep list so closing + reopening the confirm rotates the
    // key (the post-close key was either consumed by the worker or
    // unused — either way, fresh open == fresh attempt).
    [row.id, confirmOpen],
  )

  const mutation = useMutation<
    RecapEventDeleteFetchResult<RecapEventDeleteReceipt>,
    unknown,
    void
  >({
    mutationFn: () =>
      deleteRecapEvent(row.id, {
        body: { idempotency_key: idempotencyKey },
      }),
    onSuccess: (res) => {
      setNetworkError(null)
      if (res.kind === 'ok') {
        // Invalidate the admin list so the deleted row disappears.
        queryClient.invalidateQueries({
          queryKey: ['recap-events', 'admin', 'list'],
        })
        setConfirmOpen(false)
        onDeleted()
      } else if (res.kind === 'not_found') {
        // Treat as already deleted (probably another tab beat us).
        queryClient.invalidateQueries({
          queryKey: ['recap-events', 'admin', 'list'],
        })
        setConfirmOpen(false)
        onDeleted()
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'validation_error') {
        if (res.error.code === 'recap_event_not_found') {
          queryClient.invalidateQueries({
            queryKey: ['recap-events', 'admin', 'list'],
          })
          setConfirmOpen(false)
          onDeleted()
          return
        }
        setNetworkError(`Validation: ${res.error.code}`)
      } else {
        setNetworkError(res.message)
      }
    },
  })

  if (!confirmOpen) {
    return (
      <button
        type="button"
        data-testid="events-delete-open"
        data-event-id={row.id}
        onClick={() => {
          setNetworkError(null)
          setConfirmOpen(true)
        }}
        className="rounded-sm border border-rose-700 bg-rose-950/20 px-2 py-0.5 text-xs text-rose-200 hover:bg-rose-900/40"
      >
        Delete
      </button>
    )
  }

  return (
    <span
      data-testid="events-delete-confirm"
      data-event-id={row.id}
      className="inline-flex items-center gap-1"
    >
      <button
        type="button"
        data-testid="events-delete-confirm-yes"
        data-event-id={row.id}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="rounded-sm bg-rose-700 px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50"
      >
        {mutation.isPending ? 'Deleting…' : 'Confirm delete'}
      </button>
      <button
        type="button"
        data-testid="events-delete-confirm-cancel"
        data-event-id={row.id}
        onClick={() => {
          setConfirmOpen(false)
          setNetworkError(null)
        }}
        disabled={mutation.isPending}
        className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200"
      >
        Cancel
      </button>
      {networkError && (
        <span
          data-testid="events-delete-error"
          className="ml-2 text-[10px] text-rose-300"
        >
          {networkError}
        </span>
      )}
    </span>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Review-state cell (CP11 first slice)
//
// Inline four-button group on every row. Each button is disabled when
// the row already sits at that state (the Worker would 422 anyway, but
// disabling skips the round-trip and the redundant audit-row no-op).
//
// `reason` capture is intentionally minimal in this slice: hide / flag
// open a single window.prompt(). Approve / unreview never prompt — a
// reason on those is rare in practice and the slice plan calls out
// "richer reason-entry UX" as out of scope.
// ────────────────────────────────────────────────────────────────────────────

const REVIEW_STATE_BUTTONS: ReadonlyArray<{
  state: RecapReviewState
  label: string
  className: string
  promptForReason: boolean
}> = [
  {
    state: 'approved',
    label: 'Approve',
    className:
      'rounded-sm border border-emerald-700 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-30',
    promptForReason: false,
  },
  {
    state: 'hidden',
    label: 'Hide',
    className:
      'rounded-sm border border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'flagged',
    label: 'Flag',
    className:
      'rounded-sm border border-amber-700 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'unreviewed',
    label: 'Unreview',
    className:
      'rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: false,
  },
]

function reviewValidationMessage(
  err: RecapEventReviewStateValidationError,
): string {
  switch (err.code) {
    case 'invalid_review_state':
      return `Server rejected to_state="${err.received}".`
    case 'review_state_unchanged':
      return `Already at ${err.current}.`
    case 'idempotency_conflict':
      return `Conflict: that key already flipped to ${err.prior_to_state} (you sent ${err.requested_to_state}). Retrying with a fresh key.`
    case 'invalid_uuid':
      return 'Internal error: idempotency key invalid. Refresh and try again.'
    case 'invalid_enum':
      return `Invalid value for "${err.field}".`
    case 'not_found':
      return 'This event no longer exists.'
    default:
      return 'Review-state flip failed.'
  }
}

function ReviewStateCell({
  row,
  onChanged,
}: {
  row: RecapEventForAdmin
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<RecapReviewState | null>(null)

  async function flip(to: RecapReviewState, promptForReason: boolean) {
    if (row.review_state === to) return
    let reason: string | null = null
    if (promptForReason) {
      // window.prompt is the deliberate "minimal UI" choice for the
      // first slice. A richer modal lands when (and if) the workflow
      // demands it.
      const supplied = window.prompt(
        `Optional reason for flipping #${row.id} to ${to} (leave blank to skip):`,
        '',
      )
      if (supplied === null) return // operator hit Cancel
      const trimmed = supplied.trim()
      reason = trimmed.length > 0 ? trimmed : null
    }

    setError(null)
    setPending(to)
    try {
      const res = await patchRecapEventReviewState(row.id, {
        body: {
          idempotency_key: newIdempotencyKey(),
          to_state: to,
          reason,
        },
      })
      if (res.kind === 'ok') {
        onChanged()
        return
      }
      if (res.kind === 'validation_error') {
        setError(reviewValidationMessage(res.error))
        return
      }
      if (res.kind === 'csrf_error') {
        setError(`CSRF check failed: ${res.reason}`)
        return
      }
      if (res.kind === 'not_found') {
        setError('This event no longer exists.')
        return
      }
      setError(res.message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      data-testid="events-review-state-cell"
      data-event-id={row.id}
      data-review-state={row.review_state}
      className="flex flex-col gap-1"
    >
      <span className="text-xs text-slate-300 uppercase">{row.review_state}</span>
      <div className="flex flex-wrap gap-1">
        {REVIEW_STATE_BUTTONS.map((b) => {
          const disabled = row.review_state === b.state || pending !== null
          return (
            <button
              key={b.state}
              type="button"
              data-testid="events-review-state-button"
              data-event-id={row.id}
              data-to-state={b.state}
              disabled={disabled}
              onClick={() => {
                void flip(b.state, b.promptForReason)
              }}
              className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${b.className}`}
            >
              {pending === b.state ? '…' : b.label}
            </button>
          )
        })}
      </div>
      {error && (
        <span
          data-testid="events-review-state-error"
          className="text-[10px] text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Create form
// ────────────────────────────────────────────────────────────────────────────

interface CreateState {
  scope: 'arc' | 'global'
  arc_id: string
  parent_event_id: string
  event_type_id: string
  title: string
  body_md: string
  happened_at: string
  display_order: string
}

const EMPTY_CREATE: CreateState = {
  scope: 'arc',
  arc_id: '',
  parent_event_id: '',
  event_type_id: '',
  title: '',
  body_md: '',
  happened_at: '',
  display_order: '',
}

function CreateEventForm({
  arcOptions,
  eventTypeOptions,
  items,
  onClose,
  onCreated,
}: {
  arcOptions: ReturnType<typeof deriveArcOptions>
  eventTypeOptions: readonly RecapEventTypeAdminItem[]
  items: readonly RecapEventForAdmin[]
  onClose: () => void
  onCreated: () => void
}) {
  const [state, setState] = useState<CreateState>(() => {
    const first = arcOptions[0]
    const init = { ...EMPTY_CREATE }
    if (first) init.arc_id = first.arc_id
    return init
  })
  // One idempotency key per form opening. Persists across retries.
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<RecapEventValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  const mutation = useMutation<
    MutationFetchResult<RecapEventMutationReceipt>,
    Error,
    RecapEventCreateRequest
  >({
    mutationFn: (body) => postRecapEvent({ body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') {
        onCreated()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setTopLevelError(`CSRF check failed: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setTopLevelError('Not found (HTTP 404).')
      } else {
        setTopLevelError(res.message)
      }
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  const parentOptions = deriveParentOptions(
    items,
    state.scope === 'arc' ? state.arc_id || null : null,
    null,
  )

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setTopLevelError(null)
    const body: RecapEventCreateRequest = {
      scope: state.scope,
      arc_id: state.scope === 'arc' ? state.arc_id || null : null,
      parent_event_id: state.parent_event_id || null,
      event_type_id: state.event_type_id || null,
      title: state.title,
      body_md: state.body_md || null,
      // Input is YYYY-MM-DDTHH:MM (datetime-local); convert to ISO
      // at the wire boundary. Empty string → null.
      happened_at: inputValueToIso(state.happened_at),
      display_order: state.display_order ? Number.parseInt(state.display_order, 10) : null,
      idempotency_key: idempotencyKey,
    }
    mutation.mutate(body)
  }

  return (
    <section
      data-testid="events-create-form"
      className="mb-6 rounded-md border border-fishstank-accent/40 bg-fishstank-surface p-4"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">Create event</h2>
        <button
          type="button"
          data-testid="events-create-cancel"
          onClick={onClose}
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-300">
          Scope
          <select
            data-testid="events-create-scope"
            value={state.scope}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                scope: e.target.value as 'arc' | 'global',
                arc_id: e.target.value === 'global' ? '' : s.arc_id,
              }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            <option value="arc">arc</option>
            <option value="global">global</option>
          </select>
        </label>
        <label className="text-xs text-slate-300">
          Arc
          <select
            data-testid="events-create-arc"
            value={state.arc_id}
            disabled={state.scope === 'global'}
            onChange={(e) => setState((s) => ({ ...s, arc_id: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 disabled:opacity-40"
          >
            <option value="">(none)</option>
            {arcOptions.map((a) => (
              <option key={a.arc_id} value={a.arc_id}>
                {a.title} ({a.slug})
              </option>
            ))}
          </select>
          {errorField === 'arc_id' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Title
          <input
            type="text"
            data-testid="events-create-title"
            value={state.title}
            onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
          {errorField === 'title' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Body
          <textarea
            data-testid="events-create-body"
            value={state.body_md}
            onChange={(e) => setState((s) => ({ ...s, body_md: e.target.value }))}
            rows={3}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
          {errorField === 'body_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Event type
          <select
            data-testid="events-create-type"
            value={state.event_type_id}
            onChange={(e) =>
              setState((s) => ({ ...s, event_type_id: e.target.value }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            <option value="">(none)</option>
            {eventTypeOptions.map((et) => (
              <option key={et.id} value={et.id}>
                {et.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-300">
          Parent event
          <select
            data-testid="events-create-parent"
            value={state.parent_event_id}
            disabled={state.scope !== 'arc' || !state.arc_id}
            onChange={(e) =>
              setState((s) => ({ ...s, parent_event_id: e.target.value }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 disabled:opacity-40"
          >
            <option value="">(none — this is a root event)</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} (#{p.id})
              </option>
            ))}
          </select>
          {errorField === 'parent_event_id' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Happened at (UTC)
          <input
            type="datetime-local"
            data-testid="events-create-happened-at"
            value={state.happened_at}
            onChange={(e) =>
              setState((s) => ({ ...s, happened_at: e.target.value }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
          {errorField === 'happened_at' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Display order
          <input
            type="number"
            data-testid="events-create-display-order"
            value={state.display_order}
            onChange={(e) =>
              setState((s) => ({ ...s, display_order: e.target.value }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
        </label>
        {topLevelError && (
          <div
            data-testid="events-create-top-error"
            className="col-span-2 rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
          >
            {topLevelError}
          </div>
        )}
        <div className="col-span-2 flex justify-end gap-2">
          <button
            type="submit"
            data-testid="events-create-submit"
            disabled={mutation.isPending}
            className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Edit form
// ────────────────────────────────────────────────────────────────────────────

interface EditState {
  title: string
  body_md: string
  happened_at: string
  display_order: string
  event_type_id: string
  parent_event_id: string
}

function rowToEditState(row: RecapEventForAdmin): EditState {
  return {
    title: row.title,
    body_md: row.body_md ?? '',
    // Convert the row's ISO happened_at into the YYYY-MM-DDTHH:MM
    // shape that <input type="datetime-local"> wants. The picker
    // displays the UTC components verbatim — same posture as the
    // prior free-text behavior.
    happened_at: isoToInputValue(row.happened_at),
    display_order: row.display_order !== null ? String(row.display_order) : '',
    event_type_id: row.event_type_id ?? '',
    parent_event_id: row.parent_event_id ?? '',
  }
}

/**
 * Build a delta body from current vs initial form state. Only fields
 * that actually changed are included. An empty string on a nullable
 * field means "set to null"; a non-empty string means "set to the
 * literal value" (after ISO / integer coercion).
 */
function buildEditDelta(
  initial: EditState,
  current: EditState,
  idempotencyKey: string,
): RecapEventEditRequest {
  const out: RecapEventEditRequest = { idempotency_key: idempotencyKey }
  if (current.title.trim() !== initial.title.trim()) {
    out.title = current.title.trim()
  }
  if (current.body_md !== initial.body_md) {
    out.body_md = current.body_md.length === 0 ? null : current.body_md
  }
  if (current.happened_at !== initial.happened_at) {
    // Both `current` and `initial` carry the input-format string
    // (YYYY-MM-DDTHH:MM); convert to ISO at the wire boundary so
    // the worker's parser sees a server-acceptable timestamp.
    out.happened_at = inputValueToIso(current.happened_at)
  }
  if (current.display_order !== initial.display_order) {
    if (current.display_order.length === 0) {
      out.display_order = null
    } else {
      const n = Number.parseInt(current.display_order, 10)
      out.display_order = Number.isFinite(n) ? n : (Number.NaN as unknown as number)
    }
  }
  if (current.event_type_id !== initial.event_type_id) {
    out.event_type_id = current.event_type_id.length === 0 ? null : current.event_type_id
  }
  if (current.parent_event_id !== initial.parent_event_id) {
    out.parent_event_id =
      current.parent_event_id.length === 0 ? null : current.parent_event_id
  }
  return out
}

/**
 * One pending attach the operator has staged but not yet committed.
 * Carries its own idempotency key so the eventual POST is replay-safe.
 */
interface PendingAttach {
  /** Stable client-side key for React list rendering. */
  uiKey: string
  entity: EntitiesAdminListItem
  role: string | null
  idempotencyKey: string
}

function EditEventForm({
  row,
  items,
  eventTypeOptions,
  onCancel,
  onSaved,
}: {
  row: RecapEventForAdmin
  items: readonly RecapEventForAdmin[]
  eventTypeOptions: readonly RecapEventTypeAdminItem[]
  onCancel: () => void
  onSaved: () => void
}) {
  const initial = useMemo(() => rowToEditState(row), [row])
  const [state, setState] = useState<EditState>(initial)
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<RecapEventValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  // Attachment-side staging state (lifted up from the panel so Save
  // can commit attach/detach + PATCH together).
  //   - pendingAttach: rows the user added in the picker but hasn't
  //     committed yet. Carry their own idempotency_key so the POST
  //     is replay-safe.
  //   - pendingDetach: ids of currently-attached chips the user
  //     marked for removal. Each detach gets a fresh idempotency_key
  //     when Save runs.
  //   - entityValidationError: surfaced from a failing attach/detach
  //     during the submit sequence.
  const [pendingAttach, setPendingAttach] = useState<PendingAttach[]>([])
  const [pendingDetach, setPendingDetach] = useState<Set<string>>(
    () => new Set(),
  )
  const [entityValidationError, setEntityValidationError] =
    useState<RecapEventEntityValidationError | null>(null)
  const [committing, setCommitting] = useState<boolean>(false)

  const mutation = useMutation<
    MutationFetchResult<RecapEventMutationReceipt>,
    Error,
    RecapEventEditRequest
  >({
    mutationFn: (body) => patchRecapEvent(row.id, { body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') onSaved()
      else if (res.kind === 'validation_error') setValidationError(res.error)
      else if (res.kind === 'csrf_error')
        setTopLevelError(`CSRF check failed: ${res.reason}`)
      else if (res.kind === 'not_found')
        setTopLevelError('This event no longer exists.')
      else setTopLevelError(res.message)
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  const parentOptions = deriveParentOptions(items, row.arc_id, row.id)

  /**
   * Run all pending attach + detach mutations sequentially. Stops on
   * the first typed validation error and surfaces it via
   * entityValidationError. Returns true on full success.
   *
   * Attach order: attaches first (so a stale-replay-blocked detach
   * doesn't leave the row in a half-edited state). Detaches last.
   */
  async function commitAttachmentChanges(): Promise<boolean> {
    for (const p of pendingAttach) {
      const res = await postRecapEventEntity({
        body: {
          recap_event_id: row.id,
          entity_id: p.entity.id,
          role: p.role,
          idempotency_key: p.idempotencyKey,
        },
      })
      if (res.kind !== 'ok') {
        if (res.kind === 'validation_error') {
          setEntityValidationError(res.error)
        } else if (res.kind === 'csrf_error') {
          setTopLevelError(`CSRF check failed: ${res.reason}`)
        } else if (res.kind === 'not_found') {
          setTopLevelError('This event no longer exists.')
        } else {
          setTopLevelError(res.message)
        }
        return false
      }
    }
    for (const attachmentId of pendingDetach) {
      const res = await deleteRecapEventEntity(attachmentId, {
        body: { idempotency_key: newIdempotencyKey() },
      })
      if (res.kind !== 'ok') {
        if (res.kind === 'validation_error') {
          setEntityValidationError(res.error)
        } else if (res.kind === 'csrf_error') {
          setTopLevelError(`CSRF check failed: ${res.reason}`)
        } else if (res.kind === 'not_found') {
          // Race: someone else detached it. Treat as already-applied.
          continue
        } else {
          setTopLevelError(res.message)
        }
        return false
      }
    }
    return true
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setEntityValidationError(null)
    setTopLevelError(null)

    const body = buildEditDelta(initial, state, idempotencyKey)
    const fieldKeys = Object.keys(body).filter((k) => k !== 'idempotency_key')
    const hasFieldChanges = fieldKeys.length > 0
    const hasAttachmentChanges =
      pendingAttach.length > 0 || pendingDetach.size > 0

    // Both empty → emit the same "no editable fields" copy the
    // PATCH parser would have surfaced, without a wasted round-trip.
    if (!hasFieldChanges && !hasAttachmentChanges) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }

    setCommitting(true)
    try {
      if (hasAttachmentChanges) {
        const ok = await commitAttachmentChanges()
        if (!ok) return
      }
      if (hasFieldChanges) {
        // Re-use the existing field-PATCH mutation so the UI states
        // (validation error, csrf error, etc.) flow through one
        // unified handler.
        await mutation.mutateAsync(body)
        return
      }
      // Attachment changes only — no PATCH needed. Trigger the same
      // refresh path the field-edit success branch hits.
      onSaved()
    } finally {
      setCommitting(false)
    }
  }

  const isSaving = committing || mutation.isPending

  return (
    <form
      onSubmit={submit}
      data-testid="events-edit-form"
      data-event-id={row.id}
      className="grid grid-cols-2 gap-3 bg-slate-950/60 p-4"
    >
      <div className="col-span-2 flex items-baseline justify-between">
        <span className="text-sm font-semibold text-slate-200">
          Editing event #{row.id}
          {row.scope === 'arc' && row.arc && (
            <span className="ml-2 font-mono text-xs text-slate-500">
              ({row.arc.slug})
            </span>
          )}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          scope / arc not editable in CP8
        </span>
      </div>
      <label className="col-span-2 text-xs text-slate-300">
        Title
        <input
          type="text"
          data-testid="events-edit-title"
          value={state.title}
          onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        />
        {errorField === 'title' && validationError && (
          <FieldError error={validationError} />
        )}
      </label>
      <label className="col-span-2 text-xs text-slate-300">
        Body
        <textarea
          data-testid="events-edit-body"
          value={state.body_md}
          onChange={(e) => setState((s) => ({ ...s, body_md: e.target.value }))}
          rows={3}
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        />
      </label>
      <label className="text-xs text-slate-300">
        Event type
        <select
          data-testid="events-edit-type"
          value={state.event_type_id}
          onChange={(e) =>
            setState((s) => ({ ...s, event_type_id: e.target.value }))
          }
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        >
          <option value="">(none)</option>
          {eventTypeOptions.map((et) => (
            <option key={et.id} value={et.id}>
              {et.label}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs text-slate-300">
        Parent event
        <select
          data-testid="events-edit-parent"
          value={state.parent_event_id}
          disabled={row.arc_id === null}
          onChange={(e) =>
            setState((s) => ({ ...s, parent_event_id: e.target.value }))
          }
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 disabled:opacity-40"
        >
          <option value="">(none — this is a root event)</option>
          {parentOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title} (#{p.id})
            </option>
          ))}
        </select>
        {errorField === 'parent_event_id' && validationError && (
          <FieldError error={validationError} />
        )}
      </label>
      <label className="text-xs text-slate-300">
        Happened at (UTC)
        <input
          type="datetime-local"
          data-testid="events-edit-happened-at"
          value={state.happened_at}
          onChange={(e) =>
            setState((s) => ({ ...s, happened_at: e.target.value }))
          }
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        />
      </label>
      <label className="text-xs text-slate-300">
        Display order
        <input
          type="number"
          data-testid="events-edit-display-order"
          value={state.display_order}
          onChange={(e) =>
            setState((s) => ({ ...s, display_order: e.target.value }))
          }
          className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        />
      </label>
      {validationError && validationError.code === 'no_editable_fields' && (
        <div
          data-testid="events-edit-no-change"
          className="col-span-2 rounded-sm border border-yellow-900 bg-yellow-950/30 px-2 py-1 text-xs text-yellow-200"
        >
          {validationMessage(validationError)}
        </div>
      )}
      {topLevelError && (
        <div
          data-testid="events-edit-top-error"
          className="col-span-2 rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
        >
          {topLevelError}
        </div>
      )}
      <div className="col-span-2">
        <EventEntitiesPanel
          row={row}
          pendingAttach={pendingAttach}
          pendingDetach={pendingDetach}
          onPendingAttachChange={setPendingAttach}
          onPendingDetachChange={setPendingDetach}
          validationError={entityValidationError}
          disabled={isSaving}
        />
      </div>
      <div className="col-span-2 flex justify-end gap-2">
        <button
          type="button"
          data-testid="events-edit-cancel"
          onClick={onCancel}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="events-edit-submit"
          data-event-id={row.id}
          disabled={isSaving}
          className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EventEntitiesPanel — admin controls for recap_event_entities.
//
// Lives inside EditEventForm. The user opens an event's edit row and
// sees the currently-attached entities as chips (portrait + name +
// optional role + delete button) plus a small inline "add" form
// sourced from /v2/staging/entities/admin/list.json. Both surfaces
// hit the new POST/DELETE /v2/staging/recap-event-entities endpoints
// with CSRF action label `recap-event-entity-write`.
//
// Design choices:
//   - No reordering. Server returns `entities` sorted by display_name
//     and we render in that order — operators don't need a manual
//     drag handle for a list this short, and audit-row noise from
//     reordering would not justify the surface.
//   - No batch attach. One-entity-at-a-time keeps the audit trail
//     legible (each attach is its own mutation_events row) and
//     matches the entity-alias surface posture.
//   - Role is a free-text input bounded by
//     RECAP_EVENT_ENTITY_ROLE_MAX_LENGTH. The product brief
//     explicitly preferred "no taxonomy widening" — operators write
//     "subject", "mentioned", or whatever fits, and the next slice
//     can promote stable values to a closed list if needed.
//   - The pickable entity list filters out soft-merged rows AND
//     entities already attached on this event, since the server
//     would reject those with `already_attached`.
// ────────────────────────────────────────────────────────────────────────────

function EventEntitiesPanel({
  row,
  pendingAttach,
  pendingDetach,
  onPendingAttachChange,
  onPendingDetachChange,
  validationError,
  disabled,
}: {
  row: RecapEventForAdmin
  pendingAttach: PendingAttach[]
  pendingDetach: Set<string>
  onPendingAttachChange: (v: PendingAttach[]) => void
  onPendingDetachChange: (v: Set<string>) => void
  validationError: RecapEventEntityValidationError | null
  disabled: boolean
}) {
  const [pickerEntityId, setPickerEntityId] = useState<string>('')
  const [pickerRole, setPickerRole] = useState<string>('')

  const entitiesListQuery = useQuery<FetchResult<unknown>>({
    queryKey: ['admin', 'entities', 'list-for-attach'],
    queryFn: () =>
      fetchEntitiesForAdmin() as unknown as Promise<FetchResult<unknown>>,
    staleTime: 60_000,
  })

  // Lookup: candidate entities for the picker. Excludes already-
  // attached chips, soft-merged entities, AND pending-attach
  // selections (so the user can't queue the same entity twice).
  const candidateEntities = useMemo<EntitiesAdminListItem[]>(() => {
    const result = entitiesListQuery.data
    if (!result || result.kind !== 'ok') return []
    const all =
      (result.artifact as { body?: { entities?: EntitiesAdminListItem[] } })
        .body?.entities ?? []
    const attachedSlugs = new Set(row.entities.map((e) => e.slug))
    const pendingSlugs = new Set(pendingAttach.map((p) => p.entity.slug))
    return all.filter(
      (e) =>
        e.merged_into_entity_id === null &&
        !attachedSlugs.has(e.slug) &&
        !pendingSlugs.has(e.slug),
    )
  }, [entitiesListQuery.data, row.entities, pendingAttach])

  function stageAttach() {
    const entity = candidateEntities.find((c) => c.id === pickerEntityId)
    if (!entity) return
    const trimmedRole = pickerRole.trim()
    onPendingAttachChange([
      ...pendingAttach,
      {
        uiKey: `${entity.id}-${pendingAttach.length}-${Date.now()}`,
        entity,
        role: trimmedRole.length > 0 ? trimmedRole : null,
        idempotencyKey: newIdempotencyKey(),
      },
    ])
    setPickerEntityId('')
    setPickerRole('')
  }

  function unstageAttach(uiKey: string) {
    onPendingAttachChange(pendingAttach.filter((p) => p.uiKey !== uiKey))
  }

  function markDetach(attachmentId: string) {
    const next = new Set(pendingDetach)
    next.add(attachmentId)
    onPendingDetachChange(next)
  }

  function unmarkDetach(attachmentId: string) {
    const next = new Set(pendingDetach)
    next.delete(attachmentId)
    onPendingDetachChange(next)
  }

  const totalShown = row.entities.length + pendingAttach.length

  return (
    <section
      data-testid="event-entities-panel"
      data-event-id={row.id}
      className="rounded-sm border border-slate-800 bg-slate-950/40 p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-300">
          Tagged entities
        </h3>
        <span className="font-mono text-[10px] text-slate-500">
          {totalShown}
        </span>
      </header>

      {totalShown === 0 ? (
        <p
          data-testid="event-entities-empty"
          className="mt-2 text-xs italic text-slate-500"
        >
          No entities attached. Use the picker below to stage an attachment;
          changes commit when you click Save.
        </p>
      ) : (
        <ul
          data-testid="event-entities-chips"
          className="mt-2 flex flex-wrap gap-2"
        >
          {row.entities.map((e) => {
            const markedForDetach = pendingDetach.has(e.id)
            return (
              <CommittedEntityChip
                key={e.id}
                entity={e}
                markedForDetach={markedForDetach}
                disabled={disabled}
                onMarkDetach={() => markDetach(e.id)}
                onUnmarkDetach={() => unmarkDetach(e.id)}
              />
            )
          })}
          {pendingAttach.map((p) => (
            <PendingAttachChip
              key={p.uiKey}
              pending={p}
              disabled={disabled}
              onUnstage={() => unstageAttach(p.uiKey)}
            />
          ))}
        </ul>
      )}

      {validationError && (
        <p
          data-testid="event-entities-validation-error"
          data-error-code={validationError.code}
          className="mt-2 text-xs text-red-300"
        >
          {recapEventEntityValidationMessage(validationError)}
        </p>
      )}

      {(pendingAttach.length > 0 || pendingDetach.size > 0) && (
        <p
          data-testid="event-entities-pending-banner"
          className="mt-2 text-[10px] uppercase tracking-wider text-fishstank-accent"
        >
          {pendingAttach.length > 0 && `+${pendingAttach.length} to attach`}
          {pendingAttach.length > 0 && pendingDetach.size > 0 && ' · '}
          {pendingDetach.size > 0 && `−${pendingDetach.size} to detach`}
          {' (commit on Save)'}
        </p>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr,1fr,auto]">
        <select
          data-testid="event-entities-add-picker"
          value={pickerEntityId}
          onChange={(e) => setPickerEntityId(e.target.value)}
          disabled={disabled || candidateEntities.length === 0}
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        >
          <option value="">
            {candidateEntities.length === 0
              ? '(no entities to attach)'
              : '(select entity…)'}
          </option>
          {candidateEntities.map((e) => (
            <option key={e.id} value={e.id}>
              {e.primary_name} ({e.slug})
            </option>
          ))}
        </select>
        <input
          type="text"
          data-testid="event-entities-add-role"
          value={pickerRole}
          onChange={(e) => setPickerRole(e.target.value)}
          maxLength={RECAP_EVENT_ENTITY_ROLE_MAX_LENGTH}
          placeholder="role (optional)"
          disabled={disabled}
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
        />
        <button
          type="button"
          data-testid="event-entities-stage-attach"
          disabled={disabled || pickerEntityId === ''}
          onClick={stageAttach}
          title="Stage this attachment. It commits when you click Save."
          className="rounded-sm border border-slate-700 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-200 hover:bg-slate-800 disabled:opacity-50"
        >
          Stage
        </button>
      </div>
    </section>
  )
}

function CommittedEntityChip({
  entity,
  markedForDetach,
  disabled,
  onMarkDetach,
  onUnmarkDetach,
}: {
  entity: RecapEventEntityAdminItem
  markedForDetach: boolean
  disabled: boolean
  onMarkDetach: () => void
  onUnmarkDetach: () => void
}) {
  return (
    <li
      data-testid="event-entity-chip"
      data-attachment-id={entity.id}
      data-slug={entity.slug}
      data-marked-for-detach={markedForDetach ? 'true' : 'false'}
      className={[
        'inline-flex items-center gap-2 rounded-full border bg-slate-900 py-1 pl-1 pr-2',
        markedForDetach
          ? 'border-red-900 opacity-60 line-through'
          : 'border-slate-700',
      ].join(' ')}
    >
      <span className="relative inline-block h-6 w-6 overflow-hidden rounded-full border border-slate-700 bg-slate-800">
        {entity.portrait_url ? (
          <img
            src={entity.portrait_url}
            alt={entity.display_name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-slate-400"
          >
            {(entity.display_name || entity.slug || '?')
              .trim()
              .charAt(0)
              .toUpperCase()}
          </span>
        )}
      </span>
      <span className="text-xs text-slate-100">{entity.display_name}</span>
      {entity.role && (
        <span className="rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
          {entity.role}
        </span>
      )}
      {markedForDetach ? (
        <button
          type="button"
          data-testid="event-entity-chip-undo-detach"
          data-attachment-id={entity.id}
          onClick={onUnmarkDetach}
          disabled={disabled}
          aria-label={`Keep ${entity.display_name}`}
          title={`Keep ${entity.display_name}`}
          className="ml-1 rounded-full px-1 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200 disabled:opacity-40"
        >
          ↺
        </button>
      ) : (
        <button
          type="button"
          data-testid="event-entity-chip-detach"
          data-attachment-id={entity.id}
          onClick={onMarkDetach}
          disabled={disabled}
          aria-label={`Mark ${entity.display_name} for detach`}
          title={`Detach ${entity.display_name} (commits on Save)`}
          className="ml-1 rounded-full px-1 text-xs text-slate-500 hover:bg-slate-800 hover:text-red-300 disabled:opacity-40"
        >
          ×
        </button>
      )}
    </li>
  )
}

function PendingAttachChip({
  pending,
  disabled,
  onUnstage,
}: {
  pending: PendingAttach
  disabled: boolean
  onUnstage: () => void
}) {
  const initial = (pending.entity.primary_name || pending.entity.slug || '?')
    .trim()
    .charAt(0)
    .toUpperCase()
  return (
    <li
      data-testid="event-entity-pending-chip"
      data-slug={pending.entity.slug}
      className="inline-flex items-center gap-2 rounded-full border border-fishstank-accent bg-fishstank-accent/10 py-1 pl-1 pr-2"
    >
      <span className="relative inline-block h-6 w-6 overflow-hidden rounded-full border border-fishstank-accent bg-slate-800">
        <span
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-fishstank-accent"
        >
          {initial}
        </span>
      </span>
      <span className="text-xs text-slate-100">
        {pending.entity.primary_name}
      </span>
      {pending.role && (
        <span className="rounded-sm border border-fishstank-accent px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fishstank-accent">
          {pending.role}
        </span>
      )}
      <span className="text-[10px] uppercase tracking-wider text-fishstank-accent">
        new
      </span>
      <button
        type="button"
        data-testid="event-entity-pending-unstage"
        onClick={onUnstage}
        disabled={disabled}
        aria-label={`Unstage ${pending.entity.primary_name}`}
        title={`Unstage ${pending.entity.primary_name}`}
        className="ml-1 rounded-full px-1 text-xs text-fishstank-accent hover:bg-slate-800 disabled:opacity-40"
      >
        ×
      </button>
    </li>
  )
}

function recapEventEntityValidationMessage(
  err: RecapEventEntityValidationError,
): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Request body is invalid.'
    case 'recap_event_id_required':
      return 'A recap event is required.'
    case 'invalid_recap_event_id':
      return 'Recap event ID is malformed.'
    case 'unknown_recap_event':
      return 'That recap event no longer exists.'
    case 'entity_id_required':
      return 'Pick an entity to attach.'
    case 'invalid_entity_id':
      return 'Entity ID is malformed.'
    case 'unknown_entity':
      return 'That entity no longer exists.'
    case 'entity_soft_merged':
      return 'That entity has been merged into another and cannot be attached.'
    case 'role_too_long':
      return `Role is too long (max ${err.max} characters).`
    case 'already_attached':
      return 'That entity is already attached to this event.'
    case 'invalid_uuid':
      return 'Internal: idempotency key is malformed.'
    case 'idempotency_key_in_use':
      return 'That action was already submitted. Refresh and retry.'
    case 'attachment_not_found':
      return 'That attachment no longer exists.'
    default: {
      const _exhaustive: never = err
      return `Unknown validation error: ${JSON.stringify(_exhaustive)}`
    }
  }
}

function FieldError({ error }: { error: RecapEventValidationError }) {
  return (
    <span
      data-testid="events-field-error"
      data-error-code={error.code}
      className="mt-1 block text-xs text-red-300"
    >
      {validationMessage(error)}
    </span>
  )
}

// Surface the list-artifact type to silence unused-import warnings in
// environments where strict TS might complain about it otherwise. The
// compile step on the admin app imports it transitively via the
// worker helpers, so this is a belt-and-suspenders no-op.
export type AdminEventsListArtifact = RecapEventsForAdminListArtifact

export type AdminEventsFetchResult = FetchResult<RecapEventsForAdminListArtifact>
